'use client';

import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Button, Callout, CheckboxField, SingleSelect } from '@galapagos/design-system/web';
import type { SamlContact } from '@galapagos/shared';
import {
  clearSamlSession,
  markSamlSessionSignedIn,
  readSamlSession,
  rememberSamlSignedInContact,
  updateSamlSession,
  samlContactOptions,
  type StoredSamlSession,
} from '@/services/saml-session';
import { SIGNUP_ENTRY_ROUTE, signupRoute } from '@/services/signup-sso-routes';
import { FormActions } from '../components/FormActions';
import { PageTitle } from '../components/PageTitle';
import { PrivateFields } from '../components/PrivateFields';
import { SignupShell } from '../components/SignupShell';
import { telehealthSegments, termsSegments } from '../components/signup-consents';
import { ensureSignupStepStyle } from '../components/signup-step-style';
import { useDisclosureConsentLink } from '../hooks/useDisclosureConsentLink';
import { useLegacySignupParams } from '../hooks/useLegacySignupParams';
import { useSignupApi } from '../hooks/useSignupApi';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { useStepHeading } from '../hooks/useStepHeading';
import { startSsoEligibilityCheck } from '../profile/sso-eligibility';
import { SIGNUP_LOGIN_PAGE } from '../off-path-pages';
import { isContactOnAnotherAccount } from '../services/contact-on-another-account';
import { loginDestination, SIGNUP_DOWNLOAD_STEP } from '../services/login-resume';
import { sendFailureStatus } from '../services/send-failure-status';
import { SignupRequestError } from '../services/signup-api/signup-api';
import { useSignupFlowStore, type SignupSsoContact } from '../stores/signup-flow-store';
import { CodeEntry, ReturnToStep, SIGNUP_CONSENT, type CodeDelivery } from './CodeStepScreen';

/** The server could not send to that roster contact; another may still work. */
const CONTACT_UNDELIVERABLE = 'saml_contact_undeliverable';
/** The sealed session expired or was refused; only the regular path can go on. */
const SESSION_INVALID = 'saml_session_invalid';

function errorCode(error: unknown): string | undefined {
  return error instanceof SignupRequestError ? error.code : undefined;
}

/**
 * Drops the SSO session and starts the regular path for this employer's link,
 * as the legacy app did when SSO could not go on. The query is read when the
 * move happens, so a language switch on the way is kept.
 */
function useLeaveSso(): () => void {
  const router = useRouter();
  return useCallback(() => {
    clearSamlSession();
    router.replace(signupRoute(SIGNUP_ENTRY_ROUTE, window.location.search.slice(1)));
  }, [router]);
}

/**
 * The stored SSO session, read after mount because the static export
 * prerenders without sessionStorage: undefined until read, then the session
 * or null.
 */
function useStoredSamlSession(): StoredSamlSession | null | undefined {
  const [stored, setStored] = useState<StoredSamlSession | null | undefined>(undefined);
  useEffect(() => {
    setStored(readSamlSession());
  }, []);
  return stored;
}

interface ConsentErrors {
  terms?: string;
  telehealth?: string;
}

function SsoContactStep({ stored }: { stored: StoredSamlSession }) {
  const { t } = useTranslation(['signup', 'common']);
  const title = t('ssoContact.title');
  const headingRef = useStepHeading(title);
  const api = useSignupApi();
  const { goToNextStep } = useSignupNavigation();
  const leaveSso = useLeaveSso();
  const setSsoContact = useSignupFlowStore((state) => state.setSsoContact);
  const setOtpResendAfterSeconds = useSignupFlowStore((state) => state.setOtpResendAfterSeconds);
  const disclosureLink = useDisclosureConsentLink();
  // Roster contacts the server could not send to leave the choice.
  const [undeliverable, setUndeliverable] = useState<readonly SamlContact[]>([]);
  const [chosen, setChosen] = useState<SamlContact | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [telehealthAccepted, setTelehealthAccepted] = useState(false);
  const [errors, setErrors] = useState<ConsentErrors>({});
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  // A ref, not the state above, guards re-entry: two presses in one tick both
  // see the state before it updates.
  const submittingRef = useRef(false);
  // A send that completes after the member left this step must not store a
  // contact or move the flow on.
  const liveRef = useRef(true);
  useEffect(() => {
    liveRef.current = true;
    return () => {
      liveRef.current = false;
    };
  }, []);
  ensureSignupStepStyle();

  const options = samlContactOptions(stored).filter(
    (option) => !undeliverable.includes(option.contact),
  );
  // The first contact is chosen until the member picks another, so Send code
  // always has somewhere to send.
  const selected = options.find((option) => option.contact === chosen) ?? options[0];

  const clearError = (field: keyof ConsentErrors) =>
    setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));

  const sendCode = async (option: { contact: SamlContact; masked: string }) => {
    const epochAtSend = useSignupFlowStore.getState().flowEpoch;
    const sent = await api.initiateOtp({
      saml: { session: stored.session, contact: option.contact },
    });
    if (!liveRef.current || useSignupFlowStore.getState().flowEpoch !== epochAtSend) return;
    setSsoContact(option);
    updateSamlSession({ chosenContact: option.contact });
    setOtpResendAfterSeconds(sent.resend_available_in_seconds ?? null);
    goToNextStep('sso-contact');
  };

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (submittingRef.current || selected === undefined) return;
    const consentErrors: ConsentErrors = {};
    if (!termsAccepted) consentErrors.terms = t('account.consentRequired');
    if (!telehealthAccepted) consentErrors.telehealth = t('account.consentRequired');
    setErrors(consentErrors);
    if (consentErrors.terms || consentErrors.telehealth) return;
    submittingRef.current = true;
    setSubmitting(true);
    setStatus('');
    try {
      await sendCode(selected);
    } catch (error) {
      if (!liveRef.current) return;
      const code = errorCode(error);
      if (code === SESSION_INVALID) {
        leaveSso();
      } else if (code === CONTACT_UNDELIVERABLE) {
        setUndeliverable((current) => [...current, selected.contact]);
        setChosen(null);
        setStatus(t('ssoContact.undeliverable'));
      } else {
        setStatus(sendFailureStatus(t, error));
      }
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  return (
    <SignupShell>
      <form className="signup-step" noValidate onSubmit={onSubmit}>
        <PageTitle
          title={title}
          description={t('ssoContact.description')}
          headingRef={headingRef}
        />
        {options.length > 0 ? (
          // The masked roster contacts are personal data, so their choice
          // stays out of autocapture and replay like every typed contact.
          <PrivateFields>
            <SingleSelect
              actionId="signup.sso-contact-screen-contact.change"
              label={t('ssoContact.optionsLabel')}
              hideLabel
              value={selected?.contact ?? null}
              options={options.map((option) => ({
                value: option.contact,
                label:
                  option.contact === 'email' ? t('ssoContact.email') : t('ssoContact.textMessage'),
                description: t('ssoContact.sendCodeTo', { destination: option.masked }),
              }))}
              onSelect={(value) => {
                // The code goes to the contact chosen when Send code was pressed.
                if (submittingRef.current) return;
                setChosen(value === 'phone' ? 'phone' : 'email');
              }}
              data-testid="signup-sso-contact-select"
            />
          </PrivateFields>
        ) : null}
        <CheckboxField
          labelSegments={termsSegments(t)}
          checked={termsAccepted}
          onChange={(next) => {
            setTermsAccepted(next);
            clearError('terms');
          }}
          errorText={errors.terms}
          actionId="signup.sso-contact-screen-consent.change"
          data-testid="signup-sso-contact-consent-terms"
        />
        <CheckboxField
          labelSegments={telehealthSegments(t, disclosureLink)}
          checked={telehealthAccepted}
          onChange={(next) => {
            setTelehealthAccepted(next);
            clearError('telehealth');
          }}
          errorText={errors.telehealth}
          actionId="signup.sso-contact-screen-consent.change"
          data-testid="signup-sso-contact-consent-telehealth"
        />
        {/* The one status announcer for this step: always mounted, its text
            changes. The visible callout is hidden from assistive technology so
            the message is not announced twice. */}
        <p className="signup-visually-hidden" aria-live="polite" data-testid="signup-status">
          {status}
        </p>
        {status ? (
          <div aria-hidden="true">
            <Callout tone="error" body={status} />
          </div>
        ) : null}
        <FormActions>
          {options.length > 0 ? (
            <Button
              size="L"
              type="Primary"
              label={t('ssoContact.send')}
              htmlType="submit"
              state={submitting ? 'Loading' : 'Default'}
              actionId="signup.sso-contact-screen-continue.activate"
              data-testid="signup-continue"
            />
          ) : (
            // Nothing left to send to. The regular path lets the member type a
            // contact; the SSO session cannot go with them, because the
            // employer check only trusts a sign-in at a roster contact.
            <Button
              size="L"
              type="Primary"
              label={t('ssoContact.useAnother')}
              onPress={leaveSso}
              actionId="signup.sso-contact-screen-use-another.activate"
              data-testid="signup-sso-contact-use-another"
            />
          )}
        </FormActions>
      </form>
    </SignupShell>
  );
}

function SsoContactRoute() {
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  const stored = useStoredSamlSession();
  const leaveSso = useLeaveSso();
  const usable = stored ? samlContactOptions(stored).length > 0 : false;
  useEffect(() => {
    if (stored !== undefined && !usable) leaveSso();
  }, [stored, usable, leaveSso]);
  return stored && usable ? <SsoContactStep stored={stored} /> : null;
}

/**
 * `/sso-contact/`: an employer SSO member picks which roster contact
 * their code goes to and gives the account consents, as on production's SSO
 * screen. Without a stored SSO session the regular path starts instead.
 */
export function SsoContactStepScreen() {
  return <SsoContactRoute />;
}

function ssoDelivery(sso: SignupSsoContact, session: string): CodeDelivery {
  return {
    target: { saml: { session, contact: sso.contact } },
    channel: sso.contact === 'email' ? 'email' : 'sms',
    shownAs: sso.masked,
    stillCurrent: () => useSignupFlowStore.getState().ssoContact === sso,
  };
}

function SsoCodeStep({ sso, stored }: { sso: SignupSsoContact; stored: StoredSamlSession }) {
  const api = useSignupApi();
  const { goToStep, goToNextStep } = useSignupNavigation();
  const setLoginNotice = useSignupFlowStore((state) => state.setLoginNotice);
  const leaveSso = useLeaveSso();

  // A member who already finished signing up goes to the download page, as
  // production sends an SSO member it already knows. Everyone else continues.
  const afterSignIn = async (stillCurrent: () => boolean) => {
    markSamlSessionSignedIn();
    rememberSamlSignedInContact(sso.contact);
    const profile = await api.getRegistrationProfile().catch(() => null);
    if (!stillCurrent()) return;
    if (loginDestination(profile) === SIGNUP_DOWNLOAD_STEP) {
      clearSamlSession();
      goToStep(SIGNUP_DOWNLOAD_STEP);
      return;
    }
    // A roster birth date lets the check go now, while the session is fresh;
    // otherwise the birth-date step sends it with the member's date.
    if (readSamlSession()?.hasRosterDob === true) startSsoEligibilityCheck(api);
    goToNextStep('sso-code');
  };

  const onRejected = (error: unknown) => {
    // The roster contact is already on another member's account: the SSO
    // session cannot make one, so the member signs in on the login page.
    if (isContactOnAnotherAccount(error)) {
      clearSamlSession();
      setLoginNotice('contact-on-another-account');
      goToStep(SIGNUP_LOGIN_PAGE);
      return true;
    }
    if (errorCode(error) !== SESSION_INVALID) return false;
    leaveSso();
    return true;
  };

  // A resend to a contact that stopped taking codes returns to the choice.
  const onResendRejected = (error: unknown) => {
    const code = errorCode(error);
    if (code === SESSION_INVALID) leaveSso();
    else if (code === CONTACT_UNDELIVERABLE) goToStep('sso-contact');
    else return false;
    return true;
  };

  // A returning Pelago 1.0 member goes to the download page from the code
  // screen itself; their SSO session is done with, as for a finished member.
  const onAlreadyMember = () => {
    rememberSamlSignedInContact(sso.contact);
    clearSamlSession();
  };

  return (
    <CodeEntry
      delivery={ssoDelivery(sso, stored.session)}
      consent={SIGNUP_CONSENT}
      afterSignIn={afterSignIn}
      onAlreadyMember={onAlreadyMember}
      onRejected={onRejected}
      onResendRejected={onResendRejected}
    />
  );
}

function SsoCodeRoute() {
  // Every flow page refills the sponsor params from its own URL.
  useLegacySignupParams();
  const sso = useSignupFlowStore((state) => state.ssoContact);
  const setSsoContact = useSignupFlowStore((state) => state.setSsoContact);
  const stored = useStoredSamlSession();
  // A reload forgets the in-memory choice; the stored session still names the
  // contact the code went to, so the code step stays where it was.
  const restoredKind = sso ? undefined : stored?.chosenContact;
  const restoredMasked = restoredKind ? stored?.contacts[restoredKind]?.masked : undefined;
  useEffect(() => {
    if (restoredKind && restoredMasked !== undefined)
      setSsoContact({ contact: restoredKind, masked: restoredMasked });
  }, [restoredKind, restoredMasked, setSsoContact]);
  if (stored === undefined || restoredMasked !== undefined) return null;
  return sso && stored ? (
    <SsoCodeStep sso={sso} stored={stored} />
  ) : (
    <ReturnToStep page="sso-contact" />
  );
}

/**
 * `/sso-code/`: checks the code sent to the chosen roster contact and
 * signs the member in. Without a stored session or a chosen contact the
 * contact choice opens again.
 */
export function SsoCodeStepScreen() {
  return <SsoCodeRoute />;
}
